import { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { Box, Flex, FlexProps } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useCreateProduct, useUploadDesignFile, useToast, useSyncBucketFiles, useRunItem } from 'hooks';
import { getToken } from 'utils/auth';
import { IData } from 'types';
import { Button } from 'components/UI';
import TextWhite from 'components/common/TextWhite/TextWhite';
import HighlightButton from 'components/common/Buttons/HighlightButton';
import ErrorPopup from 'components/common/Popup/ErrorPopup';
import { socketUrl } from 'constants/sockets';
import AppRoutes from 'constants/routes';
import ProductField from '../ProductField';
import ProductSection from '../ProductSection';
import { IParameter, IProductResource } from '../../../../server/types';
import UploadLoader from '../UploadLoader';

const UploadZipFormField = (props: FlexProps) => (
  <Flex
    sx={{
      flex: 1,
      flexDir: 'column',
      justifyContent: 'flex-start',
      bgColor: 'base2Colour.500',
      m: 1.5,
      alignItems: 'space-between',
      fontSize: 'sm',
      color: 'base2FontColour.500',
    }}
    {...props}
  />
);

type UploadZipFormProps = {
  setOverflow?: ((arg0?: boolean | undefined) => void) | undefined;
};
const UploadZipForm = ({ setOverflow }: UploadZipFormProps) => {
  const navigate = useNavigate();
  const [createProduct, { loading: createProductLoading }] = useCreateProduct();
  const [uploadDesignFile, { loading: uploadDesignFileLoading, progress }] = useUploadDesignFile();
  const [runItem] = useRunItem();
  const [syncBucketFiles, { loading: syncBucketFilesLoading }] = useSyncBucketFiles();
  const [showToast] = useToast();
  const [displayError, setDisplayError] = useState(false);
  const [runItemLoading, setRunItemLoading] = useState(false);
  const [initialState, setUpdatedState] = useState({
    preload: false,
    displayName: '',
    name: '',
    loading: false,
    bucket: '',
    file: {} as Blob,
    objectName: '',
    projectFile: '',
    drawingFileName: '',
    rootFileName: '',
    saveToBucket: '',
    tempBucket: '',
    socketId: '',
    quoteObjectName: '',
    quoteBucket: '',
    quoteFile: {} as Blob,
    message: '',
    reportUrl: '',
    uploadZip: true,
    uploadQuote: true,
    version: 'Autodesk.Inventor+26',
  });

  const socketRef = useRef<ReturnType<typeof io>>();

  const extractParamsCallback = async ({
    status,
    parameters,
    ...data
  }: {
    status: string;
    parameters: IParameter[];
    error?: string;
    reportUrl?: string;
  }) => {
    if (status !== 'success') {
      const { error, reportUrl } = data;
      setDisplayError(!!reportUrl);
      setUpdatedState({
        ...initialState,
        loading: false,
        message: error as string,
        reportUrl: reportUrl as string,
      });

      return;
    }

    const {
      preload,
      displayName,
      name,
      saveToBucket,
      tempBucket,
      quoteBucket,
      quoteObjectName,
      drawingFileName,
      projectFile,
      rootFileName,
      objectName,
      bucket,
      version,
    } = initialState;

    const newProduct: IProductResource = {
      preload,
      displayName,
      name,
      parameters,
      parameterList: [
        { title: 'Inputs', pList: typeof parameters === 'object' ? Object.keys(parameters) : [], primary: true },
      ],
      quote: { objectName: quoteObjectName, bucket: quoteBucket },
      refImage: '',
      saveToBucket,
      tempBucket,
      thumbnailImage: '',
      type: initialState.rootFileName.toLowerCase().includes('.iam') ? 'assembly' : 'part',
      viewable: { objectName: `${initialState.name}viewable.zip`, bucket },
      zipFile: { drawingFileName, projectFile, rootFileName, file: { objectName, bucket } },
      version: version as `Autodesk.Inventor+${26 | 27 | 28 | 29 | 30}`,
    };

    setRunItemLoading(false);

    await Promise.all([createProduct(newProduct), syncBucketFiles()]);

    showToast({
      message: 'Product has been created.',
      status: 'success',
    });

    setUpdatedState((prevInitialState) => ({
      ...prevInitialState,
      loading: false,
    }));

    navigate(AppRoutes.ProductManager);
  };

  const handleSocketInit = () => {
    if (!socketRef.current) return;
    socketRef.current.off('Extract Params Callback');
    socketRef.current.on(
      'Extract Params Callback',
      (data: { status: string; parameters: IParameter[]; error?: string; reportUrl?: string }) => {
        extractParamsCallback(data);
      }
    );
  };

  useEffect(() => {
    socketRef.current = io(socketUrl);

    return () => {
      socketRef.current?.disconnect();
    };
  }, []);

  useEffect(() => {
    handleSocketInit();
  }, [initialState]);

  const updateValue = (name: string, value: string) =>
    setUpdatedState((prevInitialState) => ({
      ...prevInitialState,
      [name]: value,
    }));

  const updateFile = (
    fileStateProp: string,
    file: Blob | string,
    objectNameProp: string,
    objectName: string,
    isZip: boolean,
    upload: any
  ) => {
    if (!upload) {
      setUpdatedState((prevInitialState) => ({
        ...prevInitialState,
        [fileStateProp]: file,
        [objectNameProp]: objectName,
        [isZip ? 'uploadZip' : 'uploadQuote']: false,
      }));
    } else if (isZip) {
      setUpdatedState((prevInitialState) => ({
        ...prevInitialState,
        [fileStateProp]: file,
        [objectNameProp]: objectName,
        uploadZip: true,
      }));
    } else {
      setUpdatedState((prevInitialState) => ({
        ...prevInitialState,
        [fileStateProp]: file,
        [objectNameProp]: objectName,
        uploadQuote: true,
      }));
    }
  };

  const uploadFile = async (token: string, file: Blob, bucket: string | Blob) => {
    const formData = new FormData();
    formData.append('bucket', bucket);
    formData.append('fileSize', `${file.size}`);
    formData.append('file', file);

    const res = await uploadDesignFile(formData);
    return res;
  };

  const onUploadClick = async () => {
    const {
      file,
      bucket,
      objectName,
      name,
      projectFile,
      rootFileName,
      quoteFile,
      bucket: quoteBucket,
      version,
    } = initialState;

    const stateValues = Object.values(initialState);

    const blankValues = stateValues.filter((value) => value === '' || value === undefined);

    if (blankValues.length > 4) {
      showToast({
        message: 'Please complete all fields.',
        status: 'error',
      });
      return;
    }

    const fileSize = file ? (file as Blob).size / 1024 / 1024 : 0;

    if (fileSize > 3000) {
      showToast({
        status: 'error',
        message: 'The file selected is too large (max file size is 3 GB).',
      });
      return;
    }

    setUpdatedState((prevInitialState) => ({
      ...prevInitialState,
      loading: true,
    }));

    let zipFileStatus = 'OK';
    let quoteFileStatus = 'OK';

    const token = getToken();
    if (initialState.uploadZip) {
      zipFileStatus = (await uploadFile(token, file, bucket)) as string;
    }
    if (initialState.uploadQuote) {
      quoteFileStatus = (await uploadFile(token, quoteFile, quoteBucket)) as string;
    }

    const socketId = socketRef.current?.id;

    if (zipFileStatus === 'OK' && quoteFileStatus === 'OK') {
      const workitem: IData = {
        workitem: 'extractParams',
        socketId,
        file,
        bucket,
        objectName,
        name,
        projectFile,
        rootFileName,
        version,
      };

      setRunItemLoading(true);

      await runItem(workitem);
    }
  };

  const getStepText = () => {
    if (uploadDesignFileLoading) {
      return `Uploading ${initialState.uploadQuote ? 'quote' : 'zip file'}...`;
    }
    if (runItemLoading) {
      return 'Extracting zip file...';
    }

    if (createProductLoading || syncBucketFilesLoading) {
      return 'Creating the product...';
    }

    return null;
  };

  const renderLoader = initialState.loading ? <UploadLoader progress={progress} text={getStepText()} /> : null;

  const renderFields = () => (
    <Box>
      <UploadZipFormField>
        <ProductField
          name="preload"
          displayName="Preloaded Model"
          edit
          fieldType="checkbox"
          onChange={updateValue}
          data={initialState.preload}
        />
      </UploadZipFormField>
      <UploadZipFormField>
        <ProductField
          name="displayName"
          displayName="Display Name"
          edit
          placeholder="Enter the display name"
          fieldType="text"
          onChange={updateValue}
          data={initialState.displayName}
        />
      </UploadZipFormField>
      <UploadZipFormField>
        <ProductField
          name="name"
          displayName="Name"
          edit
          placeholder="Enter the name"
          fieldType="text"
          onChange={updateValue}
          data={initialState.name}
        />
      </UploadZipFormField>
      <UploadZipFormField>
        <ProductField
          name="version"
          displayName="Inventor Version"
          edit
          placeholder="Enter the inventor version"
          fieldType="select"
          onChange={updateValue}
          data={initialState.version as string}
        />
      </UploadZipFormField>
      <UploadZipFormField>
        <ProductField
          name="bucket"
          displayName="Bucket"
          buttonText="Select"
          edit
          fieldType="bucketSelect"
          policy="persistent"
          onChange={updateValue}
          data={initialState.bucket}
          setOverflow={setOverflow}
        />
      </UploadZipFormField>
      <UploadZipFormField>
        <ProductField
          name="objectName"
          displayName="File"
          buttonText="Select"
          edit
          fieldType="fileSelect"
          fileStateProp="file"
          onChange={updateFile}
          fileBucketSelectProps={{
            fileType: 'zip',
          }}
          data={initialState.objectName}
          bucketName={initialState.bucket}
          isZip
          showSelect
          fileLimitMessage="3GB"
        />
      </UploadZipFormField>
      <UploadZipFormField>
        <ProductField
          name="projectFile"
          displayName="Project File"
          buttonText="Select"
          placeholder="Enter the project file"
          edit
          fieldType="text"
          onChange={updateValue}
          data={initialState.projectFile}
        />
      </UploadZipFormField>
      <UploadZipFormField>
        <ProductField
          name="drawingFileName"
          displayName="Drawing File"
          buttonText="Select"
          edit
          placeholder="Enter the drawing file"
          fieldType="text"
          onChange={updateValue}
          data={initialState.drawingFileName}
        />
      </UploadZipFormField>
      <UploadZipFormField>
        <ProductField
          name="rootFileName"
          displayName="Root File"
          buttonText="Select"
          edit
          placeholder="Enter the root file"
          fieldType="text"
          onChange={updateValue}
          data={initialState.rootFileName}
        />
      </UploadZipFormField>
      <UploadZipFormField>
        <ProductField
          name="tempBucket"
          displayName="Transient Bucket"
          buttonText="Select"
          edit
          fieldType="bucketSelect"
          policy="transient"
          onChange={updateValue}
          data={initialState.tempBucket}
          setOverflow={setOverflow}
        />
      </UploadZipFormField>
      <UploadZipFormField>
        <ProductField
          name="saveToBucket"
          displayName="30 Day Bucket"
          buttonText="Select"
          edit
          fieldType="bucketSelect"
          policy="temporary"
          onChange={updateValue}
          data={initialState.saveToBucket}
          setOverflow={setOverflow}
        />
      </UploadZipFormField>
      <UploadZipFormField>
        <ProductField
          name="quoteBucket"
          displayName="Quote Bucket"
          buttonText="Select"
          edit
          fieldType="bucketSelect"
          policy="persistent"
          onChange={updateValue}
          data={initialState.quoteBucket}
          setOverflow={setOverflow}
        />
      </UploadZipFormField>
      <UploadZipFormField>
        <ProductField
          name="quoteObjectName"
          displayName="Quote File"
          buttonText="Select"
          edit
          fieldType="fileSelect"
          onChange={updateFile}
          fileStateProp="quoteFile"
          fileBucketSelectProps={{
            fileType: 'doc',
          }}
          bucketName={initialState.quoteBucket}
          data={initialState.quoteObjectName}
          isZip={false}
          showSelect
        />
      </UploadZipFormField>
    </Box>
  );

  if (initialState.preload) {
    return (
      <Flex flexDir="column" justifyContent="center">
        {renderLoader}
        <Flex flexDir="column" px={36}>
          <ProductSection name="DEFINE NEW PRODUCT DETAILS">{renderFields()}</ProductSection>
        </Flex>
        <Button onClick={onUploadClick}>
          <HighlightButton first="UPLOAD +" second="CONTINUE" />
        </Button>
      </Flex>
    );
  }

  return (
    <Flex flexDir="column" justifyContent="center">
      {renderLoader}
      <Flex flexDir="column" px={36}>
        <ProductSection name="DEFINE NEW PRODUCT DETAILS">{renderFields()}</ProductSection>
      </Flex>
      <Button size="sm" onClick={onUploadClick}>
        <TextWhite>UPLOAD</TextWhite>
      </Button>

      <ErrorPopup
        isOpen={displayError}
        message={initialState.message}
        reportUrl={initialState.reportUrl}
        closePopup={() => setDisplayError(false)}
      />
    </Flex>
  );
};

export default UploadZipForm;
